import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API_BASE_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Increased to 30 seconds for general requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
);

class GameService {
  /**
   * Get all games with optional filtering
   * @param {Object} filters - Filter object
   * @returns {Promise<Object>} Games list response
   */
  async getGames(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filter parameters
      if (filters.players) params.append('players', filters.players);
      if (filters.strictPlayers) params.append('strictPlayers', filters.strictPlayers);
      if (filters.maxPlaytime) params.append('maxPlaytime', filters.maxPlaytime);
      if (filters.minComplexity) params.append('minComplexity', filters.minComplexity);
      if (filters.maxComplexity) params.append('maxComplexity', filters.maxComplexity);
      if (filters.includeTags?.length) params.append('includeTags', filters.includeTags.join(','));
      if (filters.excludeTags?.length) params.append('excludeTags', filters.excludeTags.join(','));
      if (filters.coOpOnly) params.append('coOpOnly', filters.coOpOnly);
      if (filters.ownedOnly) params.append('ownedOnly', filters.ownedOnly);
      if (filters.wishlistOnly) params.append('wishlistOnly', filters.wishlistOnly);
      if (filters.tryOnly) params.append('tryOnly', filters.tryOnly);
      if (filters.excludeExpansions) params.append('excludeExpansions', filters.excludeExpansions);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/games?${params}`);
      
      // Transform the response to match frontend expectations
      return {
        games: response.data.games.map(this.transformGameFromAPI),
        total: response.data.total,
        filtered: response.data.filtered
      };
    } catch (error) {
      console.error('Error fetching games:', error);
      throw new Error('Failed to fetch games');
    }
  }

  /**
   * Add a new game to the collection
   * @param {Object} gameData - Game data to create
   * @returns {Promise<Object>} Created game
   */
  async addGame(gameData) {
    try {
      const payload = this.transformGameForAPI(gameData);
      const response = await api.post('/games', payload);
      return this.transformGameFromAPI(response.data);
    } catch (error) {
      console.error('Error adding game:', error);
      throw new Error('Failed to add game');
    }
  }

  /**
   * Update an existing game
   * @param {string} gameId - Game ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated game
   */
  async updateGame(gameId, updates) {
    try {
      const payload = this.transformGameForAPI(updates);
      const response = await api.put(`/games/${gameId}`, payload);
      return this.transformGameFromAPI(response.data);
    } catch (error) {
      console.error('Error updating game:', error);
      throw new Error('Failed to update game');
    }
  }

  /**
   * Update game status (owned, wishlist, try, rating)
   * @param {string} gameId - Game ID
   * @param {Object} status - Status updates
   * @returns {Promise<Object>} Updated game
   */
  async updateGameStatus(gameId, status) {
    try {
      const payload = {};
      
      if ('owned' in status) payload.owned = status.owned;
      if ('wishlist' in status) payload.wishlist = status.wishlist;
      if ('try' in status) payload.try = status.try;
      if ('rating' in status) payload.rating = status.rating;
      if ('lastPlayed' in status) payload.lastPlayed = status.lastPlayed;

      const response = await api.put(`/games/${gameId}/status`, payload);
      return this.transformGameFromAPI(response.data);
    } catch (error) {
      console.error('Error updating game status:', error);
      throw new Error('Failed to update game status');
    }
  }

  /**
   * Delete a game from the collection
   * @param {string} gameId - Game ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteGame(gameId) {
    try {
      await api.delete(`/games/${gameId}`);
      return true;
    } catch (error) {
      console.error('Error deleting game:', error);
      throw new Error('Failed to delete game');
    }
  }

  /**
   * Get a random game based on filters
   * @param {Object} filters - Filter object
   * @returns {Promise<Object>} Random game
   */
  async getRandomGame(filters = {}) {
    try {
      const payload = this.transformFiltersForAPI(filters);
      const response = await api.post('/games/random', payload);
      return this.transformGameFromAPI(response.data);
    } catch (error) {
      console.error('Error getting random game:', error);
      throw new Error('Failed to get random game');
    }
  }

  /**
   * Get collection statistics
   * @returns {Promise<Object>} Statistics
   */
  async getStats() {
    try {
      const response = await api.get('/stats');
      return response.data;
    } catch (error) {
      console.error('Error getting stats:', error);
      throw new Error('Failed to get statistics');
    }
  }

  /**
   * Transform game data from API format to frontend format
   * @param {Object} apiGame - Game from API
   * @returns {Object} Transformed game
   */
  transformGameFromAPI(apiGame) {
    return {
      id: apiGame._id,
      name: apiGame.name,
      players: apiGame.players,
      playtime: apiGame.playtime,
      complexity: apiGame.complexity,
      isCoOp: apiGame.isCoOp,
      tags: apiGame.tags,
      description: apiGame.description,
      owned: apiGame.owned,
      wishlist: apiGame.wishlist,
      try: apiGame.try,
      weight: apiGame.weight,
      image: apiGame.image,
      addedDate: apiGame.addedDate,
      lastPlayed: apiGame.lastPlayed,
      rating: apiGame.rating,
      playCount: apiGame.playCount || 0,
      notes: apiGame.notes,
      bggId: apiGame.bggId
    };
  }

  /**
   * Transform game data from frontend format to API format
   * @param {Object} frontendGame - Game from frontend
   * @returns {Object} Transformed game
   */
  transformGameForAPI(frontendGame) {
    const apiGame = {
      name: frontendGame.name,
      players: frontendGame.players || {
        min: frontendGame.minPlayers || 1,
        max: frontendGame.maxPlayers || 4
      },
      playtime: frontendGame.playtime,
      complexity: frontendGame.complexity,
      isCoOp: frontendGame.isCoOp || false,
      tags: Array.isArray(frontendGame.tags) 
        ? frontendGame.tags 
        : (frontendGame.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag),
      description: frontendGame.description,
      owned: frontendGame.owned !== undefined ? frontendGame.owned : true,
      wishlist: frontendGame.wishlist || false,
      try: frontendGame.try || false,
      weight: frontendGame.weight || 1,
      image: frontendGame.image,
      notes: frontendGame.notes,
      rating: frontendGame.rating,
      playCount: frontendGame.playCount,
      bggId: frontendGame.bggId
    };

    // Only include non-undefined values
    return Object.fromEntries(
      Object.entries(apiGame).filter(([_, value]) => value !== undefined)
    );
  }

  /**
   * Transform filters from frontend format to API format
   * @param {Object} frontendFilters - Filters from frontend
   * @returns {Object} Transformed filters
   */
  transformFiltersForAPI(frontendFilters) {
    return {
      players: frontendFilters.players,
      strictPlayers: frontendFilters.strictPlayers,
      maxPlaytime: frontendFilters.maxPlaytime,
      minComplexity: frontendFilters.minComplexity,
      maxComplexity: frontendFilters.maxComplexity,
      includeTags: frontendFilters.includeTags,
      excludeTags: frontendFilters.excludeTags,
      coOpOnly: frontendFilters.coOpOnly,
      ownedOnly: frontendFilters.ownedOnly,
      wishlistOnly: frontendFilters.wishlistOnly,
      tryOnly: frontendFilters.tryOnly,
      search: frontendFilters.search
    };
  }

  /**
   * PIN Management Methods
   */

  /**
   * Get PIN status
   * @returns {Promise<Object>} PIN status
   */
  async getPinStatus() {
    try {
      const response = await api.get('/settings/pin');
      return response.data;
    } catch (error) {
      console.error('Error getting PIN status:', error);
      throw new Error('Failed to get PIN status');
    }
  }

  /**
   * Set PIN
   * @param {string} pinHash - Hashed PIN
   * @returns {Promise<Object>} Response
   */
  async setPin(pinHash) {
    try {
      const response = await api.post('/settings/pin', { pin_hash: pinHash });
      return response.data;
    } catch (error) {
      console.error('Error setting PIN:', error);
      throw new Error('Failed to set PIN');
    }
  }

  /**
   * Verify PIN
   * @param {string} pinHash - Hashed PIN to verify
   * @returns {Promise<Object>} Response
   */
  async verifyPin(pinHash) {
    try {
      const response = await api.post('/settings/pin/verify', { pin_hash: pinHash });
      return response.data;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      throw new Error('Failed to verify PIN');
    }
  }

  /**
   * Remove PIN
   * @returns {Promise<Object>} Response
   */
  async removePin() {
    try {
      const response = await api.delete('/settings/pin');
      return response.data;
    } catch (error) {
      console.error('Error removing PIN:', error);
      throw new Error('Failed to remove PIN');
    }
  }

  /**
   * Price Tracking Methods
   */

  /**
   * Get price data for a game
   * @param {string} gameId - Game ID
   * @returns {Promise<Object>} Price data
   */
  async getGamePrice(gameId) {
    try {
      const response = await api.get(`/games/${gameId}/price`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No price data available
      }
      console.error('Error getting game price:', error);
      throw new Error('Failed to get game price');
    }
  }

  /**
   * Update price data for a game
   * @param {string} gameId - Game ID
   * @param {string} bggId - BoardGameGeek ID
   * @returns {Promise<Object>} Update response
   */
  async updateGamePrice(gameId, bggId) {
    try {
      // Use extended timeout for price updates (60 seconds) due to external API calls
      const response = await api.post(`/games/${gameId}/price/update`, {
        game_id: gameId,
        bgg_id: bggId
      }, {
        timeout: 60000 // 60 seconds for price API which can be slow
      });
      return response.data;
    } catch (error) {
      console.error('Error updating game price:', error);
      throw new Error('Failed to update game price');
    }
  }

  /**
   * Check price update cooldown
   * @param {string} gameId - Game ID
   * @returns {Promise<Object>} Cooldown status
   */
  async checkPriceCooldown(gameId) {
    try {
      const response = await api.get(`/games/${gameId}/price/check-cooldown`);
      return response.data;
    } catch (error) {
      console.error('Error checking price cooldown:', error);
      throw new Error('Failed to check price cooldown');
    }
  }


  /**
   * Bulk update prices for all eligible games
   * @returns {Promise<Object>} Bulk update result
   */
  async bulkUpdatePrices() {
    try {
      // Use extended timeout for bulk updates (5 minutes) as it processes multiple games
      const response = await api.post('/games/bulk-price-update', {}, {
        timeout: 300000 // 5 minutes for bulk operation
      });
      return response.data;
    } catch (error) {
      console.error('Error during bulk price update:', error);
      throw new Error('Failed to bulk update prices');
    }
  }

}

// Export singleton instance
export default new GameService();